import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormGroup,
    Stack,
    Switch,
    Tooltip,
} from '@mui/material';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import {styled, useTheme} from '@mui/material/styles';
import type * as React from 'react';
import {useEffect, useState} from 'react';
import Capacity from '@/components/Capacity/Capacity';
import CapacityNavBar from '@/components/Capacity/CapacityNavBar';
import ContainerCenteredGrid from '@/components/ContainerCenteredGrid';
import {usePreferencesState, useUpdatePreferences} from '@/context/PreferencesProvider';
import {useGetAllTeams} from '@/hooks/useGetAllTeams';
import {useGetEmployees} from '@/hooks/useGetEmployees';
import type {ClientServiceTeam} from '@/types/ClientServiceTeam';
import type {Employee} from '@/types/Employee';

const drawerWidth = 340;

export const favoritesTeamId = 999;
export const unscheduledTeamId = 998;

export const practiceAreaOptions = [
    'FileMaker',
    'ActualizeIt',
    'Cloud Native Applications',
    'Project Management',
    'Salesforce',
    'AWS',
];

const Main = styled('main', {shouldForwardProp: prop => prop !== 'open'})<{
    open ?: boolean;
}>(({theme, open}) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
    }),
}));

const DrawerHeader = styled('div')(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
}));

const PersistentDrawerLeft = () : React.ReactElement => {
    const theme = useTheme();
    const {preferences} = usePreferencesState();
    const updatePreferences = useUpdatePreferences();

    const [open, setOpen] = useState(false);
    const [teams, setTeams] = useState<ClientServiceTeam[] | null>(null);
    const [employees, setEmployees] = useState<Employee[] | null>(null);

    const getTeamsQuery = useGetAllTeams();
    const getEmployeesQuery = useGetEmployees();
    const [filtersActive, setFiltersActive] = useState(true);

    const handleTeamChange = async (teamId : number, checked : boolean) => {
        if (!checked) {
            await updatePreferences(
                {
                    capacity: {
                        selectedTeams: preferences.capacity.selectedTeams
                            .filter(selectedTeamId => selectedTeamId !== teamId),
                    },
                }
            );
        } else {
            await updatePreferences(
                {
                    capacity: {
                        selectedTeams: [...preferences.capacity.selectedTeams, teamId],
                    },
                }
            );
        }
    };

    const handlePracticeAreaChange = async (practiceArea : string, checked : boolean) => {
        if (!checked) {
            await updatePreferences(
                {
                    capacity: {
                        selectedPracticeAreas: preferences.capacity.selectedPracticeAreas
                            .filter(selectedPracticeArea => selectedPracticeArea !== practiceArea),
                    },
                }
            );
        } else {
            await updatePreferences(
                {
                    capacity: {
                        selectedPracticeAreas: [...preferences.capacity.selectedPracticeAreas, practiceArea],
                    },
                }
            );
        }
    };

    const handelOnlyCstMembersChange = async (status : boolean) => {
        await updatePreferences({capacity: {onlyCstMembers: status}});
    };

    const {selectedTeams} = preferences.capacity;
    const filterFavorites = selectedTeams.includes(favoritesTeamId);
    const filterUnscheduled = selectedTeams.includes(unscheduledTeamId);

    useEffect(() => {
        if (getTeamsQuery.data !== undefined && teams === null) {
            setTeams(getTeamsQuery.data);
        }
    }, [getTeamsQuery]);

    useEffect(() => {
        if (getEmployeesQuery.data !== undefined && employees === null) {
            setEmployees(getEmployeesQuery.data);
        }
    }, [getEmployeesQuery]);

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    if (!(teams && employees)) {
        return <ContainerCenteredGrid>
            <CircularProgress/>
        </ContainerCenteredGrid>;
    }

    return (
        <Box sx={{display: 'flex'}}>
            <CssBaseline/>
            <CapacityNavBar
                open={open}
                handleDrawerOpen={handleDrawerOpen}
            />
            <Drawer
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                    },
                }}
                variant="persistent"
                anchor="left"
                open={open}
            >
                <DrawerHeader>
                    <IconButton onClick={handleDrawerClose}>
                        {theme.direction === 'ltr' ? <ChevronLeftIcon/> : <ChevronRightIcon/>}
                    </IconButton>
                </DrawerHeader>
                <Divider/>
                <Stack
                    sx={{
                        alignItems: 'flex-end',
                        width: '100%',
                    }}
                >
                    <FormControlLabel
                        control={
                            <Switch
                                checked={filtersActive}
                                onChange={event => {
                                    setFiltersActive(event.target.checked);
                                }}
                            />
                        }
                        label="Filters"
                    />
                </Stack>
                <Divider/>
                <FormControl component="fieldset" sx={{padding: theme.spacing(2)}}>
                    <Stack sx={{justifyContent: 'center', color: '#888'}}>CS Teams</Stack>
                    <FormGroup>
                        {teams.map(team => {
                            const checked = selectedTeams.includes(team.id);
                            return (
                                <Tooltip title={team.abbreviation} key={team.id}>
                                    <FormControlLabel
                                        disabled={!filtersActive}
                                        control={(
                                            <Checkbox
                                                size="small"
                                                checked={checked}
                                                onChange={async () => handleTeamChange(team.id, !checked)}
                                            />
                                        )}
                                        label={team.name}
                                    />
                                </Tooltip>
                            );
                        })}
                    </FormGroup>
                </FormControl>
                <Divider/>
                <FormControl component="fieldset" sx={{padding: theme.spacing(2)}}>
                    <Stack sx={{justifyContent: 'center', color: '#888'}}>Include</Stack>
                    <FormGroup>
                        <FormControlLabel
                            disabled={!filtersActive}
                            control={(
                                <Checkbox
                                    size="small"
                                    checked={preferences.capacity.onlyCstMembers}
                                    onChange={async () => {
                                        await handelOnlyCstMembersChange(!preferences.capacity.onlyCstMembers);
                                    }}
                                />
                            )}
                            label="Only CST Members"
                        />
                        <FormControlLabel
                            disabled={!filtersActive}
                            control={(
                                <Checkbox
                                    size="small"
                                    checked={filterFavorites}
                                    onChange={async () => handleTeamChange(favoritesTeamId, !filterFavorites)}
                                />
                            )}
                            label="Favorites"
                        />
                        <FormControlLabel
                            disabled={!filtersActive}
                            control={(
                                <Checkbox
                                    size="small"
                                    checked={filterUnscheduled}
                                    onChange={async () => handleTeamChange(unscheduledTeamId, !filterUnscheduled)}
                                />
                            )}
                            label="Unscheduled Subcontractors"
                        />
                    </FormGroup>
                </FormControl>
                <Divider/>
                <FormControl component="fieldset" sx={{padding: theme.spacing(2)}}>
                    <Stack sx={{justifyContent: 'center', color: '#888'}}>Practice Areas</Stack>
                    <FormGroup>
                        {practiceAreaOptions.map(practiceArea => {
                            const checked = preferences.capacity.selectedPracticeAreas.includes(practiceArea);

                            return (
                                <FormControlLabel
                                    key={practiceArea}
                                    disabled={!filtersActive}
                                    control={(
                                        <Checkbox
                                            checked={checked}
                                            onChange={async () => handlePracticeAreaChange(practiceArea, !checked)}
                                            name={practiceArea}
                                            size="small"
                                        />
                                    )}
                                    label={practiceArea}
                                />
                            );
                        })}
                    </FormGroup>
                </FormControl>
            </Drawer>
            <Main open={open}>
                <DrawerHeader/>
                <Capacity
                    employees={employees}
                    filtersActive={filtersActive}
                    teams={teams}
                />
            </Main>
        </Box>
    );
};

export default PersistentDrawerLeft;
